
import Component from "vue-class-component";
import Vue from "vue";
import {
    ApplicationActions,
    ApplicationsClient,
    ApplicationViewModel, DocumentDeliveryMethodModel,
    CertificatesClient,
    factory,
    RejectApplicationRequest, MandateModel, PickUpDeliveryMethodModel, PostalDeliveryMethodModel,
    ResultError,
    SetDocumentDeliveryMethodRequest, ApplicationHistoryEventModel, PersonalInfoOptions, PersonalInfoClient, IUpdateContactInfoRequest, UpdateContactInfoRequest, CertificateDetailsModel,
    EndorsementsClient,
    EndorsementDetailsModel,
    ChangeDocumentRequest,
    DocumentDuplicateDetailsModel,
    PersonalInfoRequirementViewModel,
    StateFeeRequirementViewModel,
    PaymentViewModel
} from "@/api-client";
import { Prop } from "vue-property-decorator";
import ApplicationStateBadge from "@/features/applications/components/ApplicationStateBadge.vue";
import ConfirmWithCommentModal from "@/features/shared/components/ConfirmWithCommentModal.vue";
import ApplicationProcessingDecision from "@/features/applications/components/ApplicationProcessingDecision.vue";
import ApplicationOverviewLayout from "@/features/applications/components/ApplicationOverviewLayout.vue";
import toasts from "@/components/Toasts";
import DeliveryMethodSummary from "@/features/applications/components/DeliveryMethodSummary.vue";
import ButtonWithConfirm from "@/features/shared/components/ButtonWithConfirm.vue";
import ApplicationHistory from "@/features/applications/components/history/ApplicationHistory.vue";
import Loader from "@/features/shared/components/Loader";
import ApplicationDetails from "@/features/applications/components/ApplicationDetails.vue";
import ErrorsSummary from "@/components/ErrorsSummary.vue";
import { isNullOrWhiteSpace } from "@/extensions/string.helpers";
import { BModal } from "bootstrap-vue";
import ChangeApplicationType from "../components/ChangeApplicationType.vue";
import ContactInfo from "@/features/personProfile/views/ContactInfo.vue";
import DatePicker from "@/components/DatePicker.vue";
import waitForAllImagesLoaded from "@/components/WaitForImagesLoaded";
import renderCertificatePrintTemplate from "@/features/shared/CertificatePrintService";
import QrCode from "@/components/QrCode.vue";
import renderEndorsementPrintTemplate from "@/features/shared/EndorsementPrintService";

interface Props {
    personalInfoOptions: PersonalInfoOptions,
    application: ApplicationViewModel
}


@Component({
    components: {
        ErrorsSummary,
        ApplicationStateBadge,
        ConfirmWithCommentModal,
        ApplicationProcessingDecision,
        DeliveryMethodSummary,
        ButtonWithConfirm,
        ApplicationOverviewLayout,
        ApplicationHistory,
        ApplicationDetails,
        ChangeApplicationType,
        ContactInfo,
        DatePicker,
        QrCode
    },
})
export default class ApplicationOverview extends Vue implements Props {
    @Prop({ required: true })
    application!: ApplicationViewModel;

    @Prop({ required: true })
    actions!: ApplicationActions;

    @Prop({ required: true })
    historyEvents!: ApplicationHistoryEventModel[];

    @Prop({ required: true })
    historyLoader!: Loader;

    @Prop({ required: true })
    personalInfoOptions!: PersonalInfoOptions;

    $refs!: {
        rejectModal: ConfirmWithCommentModal,
        changeApplication: ConfirmWithCommentModal,
        changeApplicationTypeModal: BModal,
        qrCode: QrCode
    }

    errors: ResultError = factory.create(ResultError);
    deliveryMethodErrors: ResultError = factory.create(ResultError);

    isUnderstandingAddressChanging = false;

    selectedThirdPersonIdentifier: "PERSONAL_CODE" | "BIRTH_DATE" | null = null;

    certificate: CertificateDetailsModel | null = null;
    endorsement: EndorsementDetailsModel | null = null;
    printTemplateUrl: string | null = null;

    // selectedDeliveryMethodType change triggers application update, selectedDeliveryMethod change does not trigger application update
    selectedDeliveryMethod = this.application.documentDeliveryMethod ? DocumentDeliveryMethodModel.fromJS(this.application.documentDeliveryMethod.toJSON()) : null;
    selectedDeliveryMethodType: { new(): DocumentDeliveryMethodModel } | null = this.application.documentDeliveryMethod ? Object.getPrototypeOf(this.application.documentDeliveryMethod).constructor : null;
    deliveryMethods = {
        pickup: PickUpDeliveryMethodModel,
        postal: PostalDeliveryMethodModel
    };

    get userHasNotSelectedDeliveryMethod() {
        return this.selectedDeliveryMethod === undefined || this.selectedDeliveryMethod === null
    }

    get stateFeeRequirementFulfillment() {
        return this.application.requirementFulfillments.find(
            f => f.requirement instanceof StateFeeRequirementViewModel
        );
    }

    get isStateFeePaid(): boolean {
        if (!this.stateFeeRequirementFulfillment) return true; // No state fee required
        const payment = this.stateFeeRequirementFulfillment.requirementValue as PaymentViewModel;
        if (!payment) return false; // No payment method chosen

        // For BankLink: require confirmed (actual bank payment completed)
        if (payment.chosenMethod?.paymentMethod === "BankLink") {
            return payment.isConfirmed === true;
        }
        // For Transfer, OnSite, Exemption: method selection is enough
        return true;
    }

    get hasStateFeeRequirement(): boolean {
        return !!this.stateFeeRequirementFulfillment;
    }

    get hasMandate() {
        return !!(this.selectedDeliveryMethod as PickUpDeliveryMethodModel)?.mandate;
    }

    async created() {
        this.$watch(() => this.selectedDeliveryMethodType, async _ => {
            if (!this.selectedDeliveryMethodType) {
                this.selectedDeliveryMethod = null;
            } else {
                this.selectedDeliveryMethod = factory.create(this.selectedDeliveryMethodType);
            }
            await this.updateDeliveryMethod();
        });
        if (this.application.issuedDocumentId && this.actions.canViewIssuedApplicationPrintTemplate) {
            if (this.application.isForEndorsement) {
                this.endorsement = await new EndorsementsClient().details(this.application.issuedDocumentId);
                this.printTemplateUrl = (await new EndorsementsClient().printTemplate(this.application.issuedDocumentId)).url;
            }
            else if (this.application.isForCertificate) {
                this.printTemplateUrl = (await new CertificatesClient().printTemplate(this.application.issuedDocumentId)).url;
                this.certificate = await new CertificatesClient().details(this.application.issuedDocumentId);
            }
        }
    }

    async toggleMandate() {
        const selectedDeliveryMethod = this.selectedDeliveryMethod as PickUpDeliveryMethodModel;
        if (this.hasMandate) {
            selectedDeliveryMethod.mandate = null;
            const areMandateDetailsSaved = (this.application.documentDeliveryMethod as PickUpDeliveryMethodModel)?.mandate != null;

            if (areMandateDetailsSaved)
                await this.updateDeliveryMethod();
        } else {
            selectedDeliveryMethod.mandate = factory.create(MandateModel);
        }
    }

    async handleAfterPrint() {
        await new ApplicationsClient().confirmDocumentPrint(this.application.id).then(
            () => this.$emit("application-changed"),
            error => this.errors = error);
    }

    async print() {
        await this.handleAfterPrint();
        const printWindow = window.open()!;
        printWindow.document.open();
        printWindow.document.write(await this.getRenderedPrintTemplate());
        printWindow.document.close();
        printWindow.focus();
        await waitForAllImagesLoaded(printWindow);
        setTimeout(() => {
            printWindow.print();
        }, 500);
    }


    async getRenderedPrintTemplate() {
        if (!this.application.issuedDocumentId)
            throw new Error("Application has no issued document.");

        if (!this.printTemplateUrl)
            throw new Error("Print template not found.");

        if (this.certificate) {

            return await renderCertificatePrintTemplate(this.certificate, this.printTemplateUrl, this.$refs.qrCode.getBase64QrCode());
        }
        else if (this.endorsement) {
            return await renderEndorsementPrintTemplate(this.endorsement, this.printTemplateUrl, this.$refs.qrCode.getBase64QrCode());
        }
        return ""
    }

    getApplicationDocumentRouteName(): string {
        if (this.application.isForCertificate)
            return "certificate-details";

        if (this.application.isForEndorsement)
            return "endorsement-details";

        throw new Error("Unknown application document.");
    }

    get fullAddress() {
        if (this.application.seafarerContactInfoModel) {
            const flat = `${(isNullOrWhiteSpace(this.application.seafarerContactInfoModel.address.flatNumber) ? `` : `- ${this.application.seafarerContactInfoModel.address.flatNumber}`)}`;
            const district = isNullOrWhiteSpace(this.application.seafarerContactInfoModel.address.district) ? `` : `, ${this.application.seafarerContactInfoModel.address.district}`;
            const municipality = isNullOrWhiteSpace(this.application.seafarerContactInfoModel.address.municipality) ? `` : `, ${this.application.seafarerContactInfoModel.address.municipality}`;
            const province = isNullOrWhiteSpace(this.application.seafarerContactInfoModel.address.province) ? `` : `, ${this.application.seafarerContactInfoModel.address.province}`;
            const postalCode = isNullOrWhiteSpace(this.application.seafarerContactInfoModel.address.postalCode) ? `` : `, ${this.application.seafarerContactInfoModel.address.postalCode}`;
            const streetAndHouse = this.application.seafarerContactInfoModel.address.street === this.application.seafarerContactInfoModel.address.houseNumber
                ? this.application.seafarerContactInfoModel.address.street
                : `${this.application.seafarerContactInfoModel.address.street} ${this.application.seafarerContactInfoModel.address.houseNumber}`;

            return `${streetAndHouse}${flat}${district}${municipality}${province}${postalCode}`;
        }
        return "";
    }

    async saveMandate() {
        await this.updateDeliveryMethod(true);
    }

    async updateDeliveryMethod(showSuccessToast = false) {
        this.errors = factory.create(ResultError);
        const request = new SetDocumentDeliveryMethodRequest({ documentDeliveryMethod: this.selectedDeliveryMethod });

        await new ApplicationsClient().setDocumentDeliveryMethod(request, this.application.id).then(
            () => {
                // Since delivery method may be changed to 'None' and we can't change
                // the value directly due to this watcher, let's reload to reflect the change.
                this.$emit("application-changed");
                if (showSuccessToast) {
                    toasts.success(this.$ls.Applications_Overview_Toast_Success_DeliveryMethodSaved);
                }
            },
            error => this.setValidationErrors(error, this.$ls.Applications_Overview_Toast_Failure_DeliveryMethodNotSaved, "deliveryMethodErrors")
        );
    }

    async submit() {
        await this.$router.push({ name: "application-summary" });
    }

    navigateToStateFee() {
        this.$router.push({
            name: 'RequirementOverview',
            params: {
                applicationId: this.application.id,
                requirementId: this.stateFeeRequirementFulfillment!.requirementId
            }
        });
    }

    async startProcessing() {
        await new ApplicationsClient().startProcessing(this.application.id).then(
            () => this.$emit("application-changed"),
            error => this.errors = error);
    }

    async takeOverProcessing() {
        await new ApplicationsClient().takeOverProcessing(this.application.id).then(
            () => this.$emit("application-changed"),
            error => this.errors = error);
    }

    async reject(comment: string) {
        await new ApplicationsClient().reject(
            new RejectApplicationRequest({
                comment: comment
            }),
            this.application.id).then(
                () => this.$emit("application-changed"),
                error => this.errors = error);
    }

    async confirmDelivery() {
        await new ApplicationsClient().confirmDocumentDelivery(this.application.id).then(
            () => this.$emit("application-changed"),
            error => this.errors = error);
    }

    async changeApplication(comment?: string) {
        await new ApplicationsClient().changeDocument(new ChangeDocumentRequest({
            comment
        }), this.application.id).then(
            () => this.$emit("application-changed"),
            error => this.errors = error);
    }

    async confirmDeliveryAddress() {
        await new ApplicationsClient().confirmDocumentDeliveryAddress(this.application.id).then(
            () => this.$emit("application-changed"),
            error => this.errors = error);
    }

    understandAddressChanging(state: boolean) {
        this.isUnderstandingAddressChanging = state;
    }

    async saveContactInfo() {
        // contactinfo is done by updating profile contactinfo.

        const requestModel: IUpdateContactInfoRequest = {
            phoneNumber: this.application.seafarerContactInfoModel!.phoneNumber,
            email: this.application.seafarerContactInfoModel!.email,
            address: this.application.seafarerContactInfoModel!.address
        }
        await new PersonalInfoClient().updateContactInfo(new UpdateContactInfoRequest(requestModel), this.application.seafarerId)
            .then(
                () => this.$emit("application-changed"),
                error => this.errors = error);
    }

    async changeApplicationType() {
        this.$refs.changeApplicationTypeModal.show();
    }

    async deleteApplication() 
    {
        await new ApplicationsClient().delete(this.application.id).then(
            (res) => {
                toasts.success(this.$ls.Application_Deleted_Toast_Success);
                this.$emit("application-changed")
            },
            error => this.errors = error);
    }
}
